import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Trash2, Save, ArrowLeft, FileText, Clock } from 'lucide-react';
import { SessionDetails, Squad, SessionType, GroundBookingType, DocumentAttachment } from '../types';
import { format } from 'date-fns';
import DocumentUploader from '../components/DocumentUploader';
import { createSession } from '../services/sessionService';
import toast from 'react-hot-toast';

type SessionTemplate = {
  date: string;
  time: string;
  duration: number;
  location: string;
  type: SessionType;
  groundBooking: GroundBookingType;
  squads: Squad[];
  documents: DocumentAttachment[];
};

const BulkSessionCreation: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SessionTemplate[]>([
    {
      date: format(new Date(), 'yyyy-MM-dd'),
      time: '16:00',
      duration: 1.5,
      location: 'Main Field',
      type: 'Training',
      groundBooking: 'Full Pitch',
      squads: ['U13', 'U15'],
      documents: []
    }
  ]);
  const [isCreating, setIsCreating] = useState(false);

  const handleTemplateChange = (index: number, field: keyof SessionTemplate, value: any) => {
    setTemplates(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDurationChange = (index: number, value: string) => {
    const duration = parseFloat(value);
    if (!isNaN(duration) && duration > 0) {
      handleTemplateChange(index, 'duration', duration);
    }
  };

  const handleSquadChange = (index: number, squad: Squad, checked: boolean) => {
    setTemplates(prev => {
      const updated = [...prev];
      if (checked) {
        updated[index].squads = [...updated[index].squads, squad];
      } else {
        updated[index].squads = updated[index].squads.filter(s => s !== squad);
      }
      return updated;
    });
  };

  const handleAddDocument = (index: number, document: DocumentAttachment) => {
    setTemplates(prev => {
      const updated = [...prev];
      updated[index].documents = [...updated[index].documents, document];
      return updated;
    });
  };

  const handleRemoveDocument = (index: number, documentId: string) => {
    setTemplates(prev => {
      const updated = [...prev];
      updated[index].documents = updated[index].documents.filter(doc => doc.id !== documentId);
      return updated;
    });
  };

  const addTemplate = () => {
    setTemplates(prev => [
      ...prev,
      {
        date: format(new Date(), 'yyyy-MM-dd'),
        time: '16:00',
        duration: 1.5,
        location: 'Main Field',
        type: 'Training',
        groundBooking: 'Full Pitch',
        squads: ['U11'],
        documents: []
      }
    ]);
  };

  const removeTemplate = (index: number) => {
    setTemplates(prev => prev.filter((_, i) => i !== index));
  };

  const generateSessions = async () => {
    try {
      // Validate that all sessions have unique dates
      const dates = templates.map(t => t.date);
      const uniqueDates = new Set(dates);
      if (dates.length !== uniqueDates.size) {
        toast.error('Each session must have a unique date');
        return;
      }

      setIsCreating(true);

      // Create each session
      for (const template of templates) {
        if (template.squads.length === 0) continue;

        const sessionData: Omit<SessionDetails, 'id'> = {
          date: template.date,
          time: template.time,
          duration: template.duration,
          location: template.location,
          type: template.type,
          groundBooking: template.groundBooking,
          squads: [...template.squads],
          attendance: {
            'U11': [],
            'U13': [],
            'U15': [],
            'U17': []
          },
          coaches: {
            'U11': { primary: '', secondary: '' },
            'U13': { primary: '', secondary: '' },
            'U15': { primary: '', secondary: '' },
            'U17': { primary: '', secondary: '' }
          },
          focus: {
            'U11': '',
            'U13': '',
            'U15': '',
            'U17': ''
          },
          documents: [...template.documents],
          attendanceStatus: 'pending',
          locked: false
        };

        await createSession(sessionData);
      }

      toast.success(`Successfully created ${templates.length} sessions`);
      navigate('/sessions');
    } catch (error) {
      console.error('Error creating sessions:', error);
      toast.error('Failed to create sessions');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <button
          type="button"
          className="flex items-center text-navy-800 hover:text-navy-900 mb-2"
          onClick={() => navigate('/sessions')}
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Sessions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create Multiple Sessions</h1>
        <p className="mt-1 text-gray-600">
          Create multiple sessions with unique dates
        </p>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Sessions</h2>
            <button
              type="button"
              className="btn-outline flex items-center"
              onClick={addTemplate}
            >
              <Plus size={20} className="mr-2" />
              Add Session
            </button>
          </div>

          {templates.map((template, index) => (
            <div 
              key={index} 
              className="mb-6 p-4 border border-gray-200 rounded-lg relative"
            >
              <button
                type="button"
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                onClick={() => removeTemplate(index)}
                aria-label="Remove session"
              >
                <Trash2 size={20} />
              </button>

              <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <Calendar size={20} className="mr-2 text-navy-800" />
                Session #{index + 1}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={template.date}
                    onChange={(e) => handleTemplateChange(index, 'date', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={template.time}
                    onChange={(e) => handleTemplateChange(index, 'time', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label flex items-center">
                    <Clock size={16} className="mr-2" />
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={template.duration}
                    onChange={(e) => handleDurationChange(index, e.target.value)}
                    min="0.5"
                    max="5"
                    step="0.5"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter duration in hours (e.g., 1.5 for 1 hour 30 minutes)
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-input"
                    value={template.location}
                    onChange={(e) => handleTemplateChange(index, 'location', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Session Type</label>
                  <select
                    className="form-input"
                    value={template.type}
                    onChange={(e) => handleTemplateChange(index, 'type', e.target.value as SessionType)}
                  >
                    <option value="Training">Training</option>
                    <option value="Tourney Match">Match</option>
                    <option value="Practice Match">Practice Match</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Ground Booking</label>
                  <select
                    className="form-input"
                    value={template.groundBooking}
                    onChange={(e) => handleTemplateChange(index, 'groundBooking', e.target.value as GroundBookingType)}
                  >
                    <option value="Full Pitch">Full Pitch</option>
                    <option value="Half Pitch">Half Pitch</option>
                  </select>
                </div>
              </div>

              <div className="form-group mt-4">
                <label className="form-label">Squads</label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(['U11', 'U13', 'U15', 'U17'] as Squad[]).map(squad => (
                    <div key={squad} className="flex items-center">
                      <input
                        id={`template-${index}-squad-${squad}`}
                        type="checkbox"
                        checked={template.squads.includes(squad)}
                        onChange={(e) => handleSquadChange(index, squad, e.target.checked)}
                        className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300 rounded"
                      />
                      <label 
                        htmlFor={`template-${index}-squad-${squad}`} 
                        className="ml-2 block text-sm text-gray-900"
                      >
                        {squad}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group mt-4">
                <div className="flex items-center mb-2">
                  <FileText size={20} className="text-navy-800 mr-2" />
                  <label className="form-label mb-0">Attach Documents</label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Upload PDF documents or images for this session
                </p>
                <DocumentUploader 
                  documents={template.documents}
                  onAddDocument={(doc) => handleAddDocument(index, doc)}
                  onRemoveDocument={(docId) => handleRemoveDocument(index, docId)}
                />
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No sessions added yet. Click "Add Session" to create one.</p>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="btn-primary flex items-center"
              onClick={generateSessions}
              disabled={templates.length === 0 || isCreating}
            >
              <Save size={20} className="mr-2" />
              {isCreating ? 'Creating Sessions...' : 'Create Sessions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSessionCreation;